// Hotel Setup — Restaurants editor (dynamic Food experience).
// Each restaurant is independently configurable. Photos reuse the existing
// hotel asset-upload system (upload → preview → size → replace/delete → save).
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Loader2, Upload, Trash2, ChevronDown } from 'lucide-react';

const API = (typeof window !== 'undefined' ? window.location.origin : '') || '';

// One click: existing YulOnTap translation service fills FR/ES from the EN values.
export const AutoTranslateRow = ({ bases, obj, apply, idSuffix }) => {
    const { t } = useTranslation();
    const [busy, setBusy] = useState(false);
    const run = async () => {
        setBusy(true);
        try {
            const patch = {};
            for (const b of bases) {
                const src = (obj[`${b}_en`] || '').trim();
                if (!src) continue;
                const r = await api.post('/assistant/admin/translate', { text: src, source: 'en' });
                if (r.data?.fr) patch[`${b}_fr`] = r.data.fr;
                if (r.data?.es) patch[`${b}_es`] = r.data.es;
            }
            const done = Object.keys(patch).length;
            if (done) apply(patch);  // single batched update — avoids stale-closure clobber
            done ? toast.success(t('assistant.autoTranslated', 'FR + ES filled automatically'))
                 : toast.error(t('assistant.autoTranslateNoEn', 'Enter the English text first'));
        } catch { toast.error(t('assistant.autoTranslateFailed', 'Automatic translation unavailable — please fill FR/ES manually')); }
        finally { setBusy(false); }
    };
    return (
        <button type="button" onClick={run} disabled={busy}
            className="text-[11px] text-emerald-600 hover:underline disabled:opacity-40 flex items-center gap-1"
            data-testid={`assist-autotranslate-${idSuffix}`}>
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
            {t('assistant.autoTranslate', 'Auto-translate EN → FR/ES')}
        </button>
    );
};

const Tri = ({ obj, base, onChange, placeholder, idSuffix }) => (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {['en', 'fr', 'es'].map((l) => (
            <Input key={l} value={obj[`${base}_${l}`] || ''} placeholder={`${placeholder} (${l.toUpperCase()})`}
                onChange={(e) => onChange(`${base}_${l}`, e.target.value)}
                data-testid={`rest-${base}-${l}-${idSuffix}`} className="text-xs" />
        ))}
    </div>
);

export default function AssistantRestaurants({ cardId, list, onChange }) {
    const { t } = useTranslation();
    const [openId, setOpenId] = useState(null);
    const [uploading, setUploading] = useState(null);
    const fileRef = useRef(null);
    const uploadIdx = useRef(null);

    const update = (idx, key, value) =>
        onChange(list.map((r, i) => (i === idx ? { ...r, [key]: value } : r)));

    const add = () => {
        const id = `r${Date.now()}`;
        onChange([...list, {
            id, enabled: true,
            name_en: '', name_fr: '', name_es: '',
            cuisine_en: '', cuisine_fr: '', cuisine_es: '',
            description_en: '', description_fr: '', description_es: '',
            hours: '', website_url: '', menu_url: '', reservation_url: '',
            gift_card: false, photo_url: '', photo_size: 140,
        }]);
        setOpenId(id);
    };

    const remove = (idx) => {
        const r = list[idx];
        if (r.photo_url) api.delete(`/hotel/admin/delete-logo/${cardId}/rest_${r.id}`).catch(() => {});
        onChange(list.filter((_, i) => i !== idx));
    };

    const pickPhoto = (idx) => {
        uploadIdx.current = idx;
        fileRef.current?.click();
    };

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        const idx = uploadIdx.current;
        if (!file || idx === null || !list[idx]) return;
        if (!file.type.startsWith('image/')) { toast.error(t('assistant.restPhotoType', 'Please select an image file')); return; }
        if (file.size > 5 * 1024 * 1024) { toast.error(t('assistant.restPhotoTooBig', 'Image must be under 5MB')); return; }
        const r = list[idx];
        setUploading(r.id);
        try {
            const fd = new FormData();
            fd.append('logo', file);
            const res = await api.post(`/hotel/admin/upload-logo/${cardId}/rest_${r.id}`, fd,
                { headers: { 'Content-Type': 'multipart/form-data' } });
            if (res.data?.logo_url) {
                update(idx, 'photo_url', res.data.logo_url);
                toast.success(t('assistant.restPhotoUploaded', 'Photo uploaded — remember to Save'));
            }
        } catch { toast.error(t('assistant.restPhotoFailed', 'Photo upload failed')); }
        finally {
            setUploading(null);
            if (fileRef.current) fileRef.current.value = '';
        }
    };

    const removePhoto = (idx) => {
        const r = list[idx];
        api.delete(`/hotel/admin/delete-logo/${cardId}/rest_${r.id}`).catch(() => {});
        update(idx, 'photo_url', '');
    };

    return (
        <div className="space-y-2" data-testid="assist-restaurants-editor">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile}
                data-testid="rest-photo-file-input" />
            {list.map((r, i) => (
                <div key={r.id} className="border rounded-lg">
                    <button type="button" onClick={() => setOpenId(openId === r.id ? null : r.id)}
                        className="w-full flex items-center justify-between p-2.5 text-left"
                        data-testid={`rest-header-${r.id}`}>
                        <span className="text-xs font-semibold truncate">
                            {r.name_en || t('assistant.restNew', 'New restaurant')}
                            {r.enabled === false && <span className="text-muted-foreground font-normal"> — {t('assistant.restOff', 'hidden')}</span>}
                        </span>
                        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform shrink-0 ${openId === r.id ? 'rotate-180' : ''}`} />
                    </button>
                    {openId === r.id && (
                        <div className="px-2.5 pb-2.5 space-y-2">
                            <div className="flex items-center justify-between border rounded-lg p-2">
                                <span className="text-xs">{t('assistant.restEnabled', 'Visible to guests')}</span>
                                <Switch checked={r.enabled !== false} onCheckedChange={(v) => update(i, 'enabled', v)}
                                    data-testid={`rest-enabled-${r.id}`} />
                            </div>
                            <Tri obj={r} base="name" idSuffix={r.id} placeholder={t('assistant.name', 'Name')} onChange={(k, v) => update(i, k, v)} />
                            <Tri obj={r} base="cuisine" idSuffix={r.id} placeholder={t('assistant.cuisine', 'Cuisine')} onChange={(k, v) => update(i, k, v)} />
                            <AutoTranslateRow bases={['name', 'cuisine', 'description']} obj={r}
                                apply={(patch) => onChange(list.map((x, j) => (j === i ? { ...x, ...patch } : x)))} idSuffix={r.id} />
                            <Tri obj={r} base="description" idSuffix={r.id} placeholder={t('assistant.description', 'Short description')} onChange={(k, v) => update(i, k, v)} />
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <Input value={r.hours || ''} placeholder={t('assistant.hours', 'Hours (e.g. 11:00–23:00)')}
                                    onChange={(e) => update(i, 'hours', e.target.value)} className="text-xs" data-testid={`rest-hours-${r.id}`} />
                                <Input value={r.website_url || ''} placeholder={t('assistant.websiteUrl', 'Website URL')}
                                    onChange={(e) => update(i, 'website_url', e.target.value)} className="text-xs" data-testid={`rest-website-${r.id}`} />
                                <Input value={r.menu_url || ''} placeholder={t('assistant.menuUrl', 'Menu URL')}
                                    onChange={(e) => update(i, 'menu_url', e.target.value)} className="text-xs" data-testid={`rest-menu-${r.id}`} />
                                <Input value={r.reservation_url || ''} placeholder={t('assistant.reservationUrl', 'Reservation link (optional)')}
                                    onChange={(e) => update(i, 'reservation_url', e.target.value)} className="text-xs" data-testid={`rest-reservation-${r.id}`} />
                            </div>
                            <div className="flex items-center justify-between border rounded-lg p-2">
                                <div className="min-w-0">
                                    <p className="text-xs font-medium">{t('assistant.giftCardAvail', 'Gift Card Available')}</p>
                                    <p className="text-[11px] text-muted-foreground">{t('assistant.giftCardHint', 'Shows a "Gift cards available" badge. Uses your existing YulOnTap gift-card system.')}</p>
                                </div>
                                <Switch checked={!!r.gift_card} onCheckedChange={(v) => update(i, 'gift_card', v)}
                                    data-testid={`rest-giftcard-${r.id}`} />
                            </div>
                            <div className="border rounded-lg p-2 space-y-2">
                                <p className="text-xs font-medium">{t('assistant.restPhoto', 'Photo / logo')}</p>
                                {r.photo_url ? (
                                    <>
                                        <div className="w-full shrink-0 overflow-hidden rounded-lg" style={{ height: `${r.photo_size || 140}px` }}>
                                            <img src={`${API}${r.photo_url}`} alt=""
                                                className="w-full h-full object-cover" data-testid={`rest-photo-preview-${r.id}`} />
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-[11px] text-muted-foreground shrink-0">{t('assistant.restPhotoSize', 'Display size')}</span>
                                            <input type="range" min={80} max={240} step={10} value={r.photo_size || 140}
                                                onChange={(e) => update(i, 'photo_size', parseInt(e.target.value, 10))}
                                                className="flex-1" data-testid={`rest-photo-size-${r.id}`} />
                                            <span className="text-[11px] text-muted-foreground shrink-0">{r.photo_size || 140}px</span>
                                        </div>
                                        <div className="flex gap-2">
                                            <Button variant="outline" size="sm" className="text-xs" onClick={() => pickPhoto(i)}
                                                disabled={uploading === r.id} data-testid={`rest-photo-replace-${r.id}`}>
                                                {uploading === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                                                {t('assistant.replacePhoto', 'Replace')}
                                            </Button>
                                            <Button variant="ghost" size="sm" className="text-xs text-red-500" onClick={() => removePhoto(i)}
                                                data-testid={`rest-photo-remove-${r.id}`}>
                                                <Trash2 className="w-3.5 h-3.5 mr-1" /> {t('assistant.removePhoto', 'Remove')}
                                            </Button>
                                        </div>
                                    </>
                                ) : (
                                    <Button variant="outline" size="sm" className="text-xs" onClick={() => pickPhoto(i)}
                                        disabled={uploading === r.id} data-testid={`rest-photo-upload-${r.id}`}>
                                        {uploading === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                                        {t('assistant.uploadPhoto', 'Upload photo')} <span className="text-muted-foreground ml-1">(PNG/JPG, max 5MB)</span>
                                    </Button>
                                )}
                            </div>
                            <Button variant="ghost" size="sm" className="text-xs text-red-500 w-full" onClick={() => remove(i)}
                                data-testid={`rest-delete-${r.id}`}>
                                <Trash2 className="w-3.5 h-3.5 mr-1" /> {t('assistant.deleteRestaurant', 'Delete this restaurant')}
                            </Button>
                        </div>
                    )}
                </div>
            ))}
            <Button variant="outline" size="sm" className="text-xs" onClick={add} data-testid="rest-add-btn">
                + {t('assistant.addRestaurant', 'Add restaurant')}
            </Button>
        </div>
    );
}
